import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_URL, useSeo } from "@/hooks/useSeo";
import { memeItems } from "@/mocks/memes";

const ranked = [...memeItems].sort((a, b) => b.hot - a.hot);
const maxHot = ranked[0]?.hot ?? 1;

export default function Rank() {
  useSeo({
    title: "热梗榜 - 本周最受欢迎的梗图与表情包 | 131吃瓜",
    description:
      "131吃瓜热梗榜按站内浏览与保存次数排序，实时更新本周最受欢迎的梗图、表情包、神评论与网友二创，一眼看清大家都在传什么。",
    keywords: "热梗榜,梗图排行,表情包热门,131吃瓜,梗合集",
    path: "/rank",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}/rank#collection`,
          url: `${SITE_URL}/rank`,
          name: "热梗榜 - 本周最受欢迎的梗图与表情包",
          description: "按站内浏览与保存次数排序的热门内容榜单。",
          inLanguage: "zh-CN",
          isPartOf: { "@id": `${SITE_URL}/#website` },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "热梗榜", item: `${SITE_URL}/rank` },
          ],
        },
        {
          "@type": "ItemList",
          name: "131吃瓜热梗榜",
          itemListElement: ranked.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${SITE_URL}/meme/${item.id}`,
            name: item.title,
          })),
        },
      ],
    },
  });

  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3);

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: "热梗榜" }]} />
          <h1 className="mt-6 font-heading text-3xl text-foreground-950 md:text-4xl">热梗榜</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            每周按站内浏览与保存次数重新排序。上榜的基本都是最近大家反复在传的那几张，热度值越高越靠前。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {top3.map((item, index) => (
              <Link
                key={item.id}
                to={`/meme/${item.id}`}
                className={`group flex flex-col overflow-hidden rounded-lg border bg-background-50 transition-colors hover:border-primary-300 ${
                  index === 0 ? "border-primary-300 md:-translate-y-2" : "border-background-200"
                }`}
              >
                <div className="relative w-full overflow-hidden bg-background-100" style={{ aspectRatio: "4 / 3" }}>
                  <img
                    src={item.cover}
                    alt={`${item.title} 热梗榜第${index + 1}名`}
                    title={`${item.title} · 131吃瓜热梗榜`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-lg text-background-50">
                    {index + 1}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-3 p-5">
                  <span className="w-fit rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                    {item.categoryName}
                  </span>
                  <h2 className="font-heading text-lg leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {item.title}
                  </h2>
                  <div className="mt-auto flex items-center justify-between text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line text-primary-500"></i>
                      热度 {item.hot}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-download-2-line text-accent-600"></i>
                      {item.downloads}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <h2 className="mt-14 font-heading text-2xl text-foreground-950">完整榜单</h2>
          <ul className="mt-6 divide-y divide-background-200 border-y border-background-200">
            {rest.map((item, index) => (
              <li key={item.id}>
                <Link to={`/meme/${item.id}`} className="group flex items-center gap-4 py-4 md:gap-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-background-100 font-heading text-lg text-foreground-700">
                    {index + 4}
                  </span>
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-100">
                    <img
                      src={item.cover}
                      alt={`${item.title} 缩略图`}
                      title={`${item.title} · 131吃瓜热梗榜`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-heading text-base text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                      {item.title}
                    </h3>
                    <p className="mt-1 hidden truncate text-sm text-foreground-600 sm:block">{item.summary}</p>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200 sm:w-40">
                        <div
                          className="h-full rounded-full bg-accent-500"
                          style={{ width: `${Math.round((item.hot / maxHot) * 100)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-foreground-500">{item.hot}</span>
                    </div>
                  </div>
                  <span className="hidden shrink-0 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 lg:block">
                    {item.categoryName}
                  </span>
                  <span className="hidden h-5 w-5 shrink-0 items-center justify-center text-foreground-400 md:flex">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}